import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  Image,
  Button,
  Input,
  Loader,
  Pagination,
  Label,
  Icon,
} from "semantic-ui-react";
import { useAuth } from "../../../../hooks";
import { simulatorApi } from "../../../../api";
import "./SimulatorPicker.scss";

/**
 * Selector visual de simuladores.
 * Props:
 *  - value: string[]  (lista de _id seleccionados)
 *  - onChange: (ids: string[]) => void
 *  - pageSize?: number
 */
export function SimulatorPicker({ value = [], onChange, pageSize = 8 }) {
  const { accessToken } = useAuth();
  const [page, setPage] = useState(1);
  const [list, setList] = useState(null); // respuesta paginada
  const [search, setSearch] = useState("");

  // Hook SIEMPRE llamado
  const selected = useMemo(() => new Set(value), [value]);

  useEffect(() => {
    (async () => {
      try {
        const res = await simulatorApi.getSimulators(
          { page, limit: pageSize },
          accessToken,
        );
        setList(res.simulators); // { docs, totalPages, page, ... }
      } catch (e) {
        console.error(e);
      }
    })();
  }, [page, pageSize, accessToken]);

  // Hook SIEMPRE llamado (aunque list sea null)
  const filteredDocs = useMemo(() => {
    const docs = list && Array.isArray(list.docs) ? list.docs : [];
    if (!search.trim()) return docs;
    const q = search.toLowerCase();
    return docs.filter((d) => (d.title || "").toLowerCase().includes(q));
  }, [search, list]);

  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange(Array.from(next));
  };

  if (!list) return <Loader active inline="centered" />;

  return (
    <div className="sim-picker">
      <div className="sim-picker__toolbar">
        <div className="sim-picker__left">
          <Label size="small" basic>
            Seleccionados: <strong>{selected.size}</strong>
          </Label>
        </div>
        <div className="sim-picker__right">
          <Input
            icon="search"
            placeholder="Buscar por título…"
            value={search}
            onChange={(_e, { value }) => setSearch(value)}
            // Evita que Enter envíe el formulario padre
            onKeyDown={(e) => {
              if (e.key === "Enter") e.preventDefault();
            }}
          />
        </div>
      </div>

      <Card.Group itemsPerRow={4} stackable className="sim-picker__grid">
        {filteredDocs.map((sim) => {
          const isSelected = selected.has(sim._id);
          return (
            <Card
              key={sim._id}
              className={`sim-picker__card ${isSelected ? "is-selected" : ""}`}
            >
              <Image
                src={
                  sim.miniature ||
                  "https://via.placeholder.com/400x225?text=Simulator"
                }
                wrapped
                ui={false}
                alt={sim.title || "Simulator"}
              />
              <Card.Content>
                <Card.Header title={sim.title}>
                  {sim.title || "(Sin título)"}
                </Card.Header>
                <Card.Meta>{sim._id}</Card.Meta>
              </Card.Content>
              <Card.Content extra>
                <Button
                  type="button" // <-- clave: no submit
                  fluid
                  icon
                  color={isSelected ? "red" : "blue"}
                  onClick={() => toggle(sim._id)}
                  labelPosition="left"
                >
                  <Icon name={isSelected ? "minus circle" : "plus circle"} />
                  {isSelected ? "Quitar" : "Añadir"}
                </Button>
              </Card.Content>
            </Card>
          );
        })}
      </Card.Group>

      <div className="sim-picker__pagination">
        <Pagination
          totalPages={list.totalPages || 1}
          defaultActivePage={list.page || 1}
          ellipsisItem={null}
          firstItem={null}
          lastItem={null}
          onPageChange={(_e, data) => setPage(data.activePage)}
        />
      </div>

      {selected.size > 0 && (
        <div className="sim-picker__chips">
          {Array.from(selected).map((id) => (
            <Label key={id} color="blue" basic>
              {id}
              <Icon name="delete" onClick={() => toggle(id)} />
            </Label>
          ))}
        </div>
      )}
    </div>
  );
}
